import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { makeApiClient } from "../apiClient";
import { AUTH0_AUDIENCE } from "../config";
import {
  ADMIN_USER_STORAGE_KEY,
  AUTH0_BOOTSTRAP_TIMEOUT_MS,
  AUTH0_TOKEN_TIMEOUT_MS,
  BASE_URL,
  FEIDE_USER_STORAGE_KEY,
  PUBLIC_BASE_URL,
  STUDENT_USER_STORAGE_KEY,
  VIPPS_USER_STORAGE_KEY,
  withTimeout,
} from "../authUtils";
import useOptionalAuth0 from "../useOptionalAuth0";
import { AuthContext } from "./AuthContextCore";
import {
  clearStoredAuthUsers,
  persistStoredUser,
  readStoredUserSnapshot,
  removeStoredUser,
} from "./authStorage";
import {
  completeFeideSession,
  completeVippsSession,
  loginAdminSession,
  loginStudentSession,
  postLogout,
  requestAdminAccessToken,
  requestFeideAccessToken,
  requestStudentAccessToken,
  requestVippsAccessToken,
} from "./sessionApi";
import useTokenRefreshScheduler from "./useTokenRefreshScheduler";

const COOKIE_REFRESH_PROVIDERS = new Set([
  "vipps",
  "feide",
  "admin",
  "student_gate",
]);

export default function AuthProvider({ children }) {
  const {
    isAuthenticated: auth0IsAuthenticated,
    user: auth0User,
    getAccessTokenSilently: auth0GetAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
    isLoading: auth0Loading,
  } = useOptionalAuth0();

  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [vippsHasRefreshCookie, setVippsHasRefreshCookie] = useState(false);
  const [feideHasRefreshCookie, setFeideHasRefreshCookie] = useState(false);
  const [adminHasRefreshCookie, setAdminHasRefreshCookie] = useState(false);
  const [studentHasRefreshCookie, setStudentHasRefreshCookie] = useState(false);
  const [auth0BootstrapTimedOut, setAuth0BootstrapTimedOut] = useState(false);
  const bootstrapStartedRef = useRef(false);

  const clearLocalAuthState = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setVippsHasRefreshCookie(false);
    setFeideHasRefreshCookie(false);
    setAdminHasRefreshCookie(false);
    setStudentHasRefreshCookie(false);
    setIsLoading(false);
    clearStoredAuthUsers();
  }, []);

  const getVippsAccessToken = useCallback(async (force = false) => {
    if (!force && !vippsHasRefreshCookie) return null;
    try {
      const accessToken = await requestVippsAccessToken();
      setToken(accessToken);
      return accessToken;
    } catch (err) {
      console.error("Error obtaining Vipps access token:", err.message);
      return null;
    }
  }, [vippsHasRefreshCookie]);

  const getFeideAccessToken = useCallback(async (force = false) => {
    if (!force && !feideHasRefreshCookie) return null;
    try {
      const accessToken = await requestFeideAccessToken();
      setToken(accessToken);
      return accessToken;
    } catch (err) {
      console.error("Error obtaining Feide access token:", err.message);
      return null;
    }
  }, [feideHasRefreshCookie]);

  const getAdminAccessToken = useCallback(
    async (force = false) => {
      if (!force && !adminHasRefreshCookie) return null;
      try {
        const accessToken = await requestAdminAccessToken();
        setToken(accessToken);
        return accessToken;
      } catch (err) {
        console.error("Error obtaining admin access token:", err.message);
        return null;
      }
    },
    [adminHasRefreshCookie],
  );

  const getStudentAccessToken = useCallback(
    async (force = false) => {
      if (!force && !studentHasRefreshCookie) return null;
      try {
        const accessToken = await requestStudentAccessToken();
        setToken(accessToken);
        return accessToken;
      } catch (err) {
        console.error("Error obtaining student access token:", err.message);
        return null;
      }
    },
    [studentHasRefreshCookie],
  );

  const getAuth0AccessTokenWithTimeout = useCallback(
    async (options) =>
      await withTimeout(
        auth0GetAccessTokenSilently(options),
        AUTH0_TOKEN_TIMEOUT_MS,
        "Auth0 token request timed out",
      ),
    [auth0GetAccessTokenSilently],
  );

  const refreshToken = useCallback(async () => {
    if (!isAuthenticated) return null;

    try {
      if (user?.provider === "vipps") {
        return await getVippsAccessToken();
      }
      if (user?.provider === "feide") {
        return await getFeideAccessToken();
      }
      if (user?.provider === "admin") {
        return await getAdminAccessToken();
      }
      if (user?.provider === "student_gate") {
        return await getStudentAccessToken();
      }
      if (user?.provider === "auth0") {
        const newToken = await getAuth0AccessTokenWithTimeout(
          AUTH0_AUDIENCE ? { audience: AUTH0_AUDIENCE } : undefined,
        );
        setToken(newToken);
        return newToken;
      }
    } catch (err) {
      console.error("refreshToken error:", err);
    }

    return null;
  }, [
    getAdminAccessToken,
    getAuth0AccessTokenWithTimeout,
    getFeideAccessToken,
    getStudentAccessToken,
    getVippsAccessToken,
    isAuthenticated,
    user,
  ]);

  const clearScheduledRefresh = useTokenRefreshScheduler({
    token,
    refreshToken,
    enabled: Boolean(token && COOKIE_REFRESH_PROVIDERS.has(user?.provider)),
  });

  const postLogoutRequest = useCallback(
    async (path) => await postLogout(path),
    [],
  );

  const handleLogout = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    clearScheduledRefresh();

    const provider = user?.provider || null;
    clearLocalAuthState();

    let shouldResetLoggingOut = true;
    try {
      await Promise.allSettled([
        postLogoutRequest("/admin/logout"),
        postLogoutRequest("/student/logout"),
        postLogoutRequest("/vipps/logout"),
        postLogoutRequest("/feide/logout"),
      ]);

      if (provider === "auth0") {
        shouldResetLoggingOut = false;
        auth0Logout({ returnTo: window.location.origin });
        return;
      }

      if (typeof window !== "undefined") {
        shouldResetLoggingOut = false;
        window.location.replace(window.location.origin);
        return;
      }
    } finally {
      if (shouldResetLoggingOut) {
        setIsLoggingOut(false);
      }
    }
  }, [
    auth0Logout,
    clearLocalAuthState,
    clearScheduledRefresh,
    isLoggingOut,
    postLogoutRequest,
    user,
  ]);

  const setVippsAuth = useCallback(async ({
    user: vippsUser,
    token: vippsIdToken,
    completionToken,
  } = {}) => {
    try {
      const { accessToken, user: resolvedUser } = await completeVippsSession({
        user: vippsUser,
        idToken: vippsIdToken,
        completionToken,
      });

      persistStoredUser(VIPPS_USER_STORAGE_KEY, resolvedUser);
      setIsAuthenticated(true);
      setUser(resolvedUser);
      setVippsHasRefreshCookie(true);
      setToken(accessToken);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error("Error setting Vipps auth:", err.message);
      removeStoredUser(VIPPS_USER_STORAGE_KEY);
      setIsAuthenticated(false);
      setUser(null);
      setVippsHasRefreshCookie(false);
      setToken(null);
      setIsLoading(false);
      return false;
    }
  }, []);

  const setFeideAuth = useCallback(async ({
    user: feideUser,
    token: feideIdToken,
    completionToken,
  } = {}) => {
    try {
      const { accessToken, user: resolvedUser } = await completeFeideSession({
        user: feideUser,
        idToken: feideIdToken,
        completionToken,
      });

      persistStoredUser(FEIDE_USER_STORAGE_KEY, resolvedUser);
      setIsAuthenticated(true);
      setUser(resolvedUser);
      setFeideHasRefreshCookie(true);
      setToken(accessToken);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error("Error setting Feide auth:", err.message);
      removeStoredUser(FEIDE_USER_STORAGE_KEY);
      setIsAuthenticated(false);
      setUser(null);
      setFeideHasRefreshCookie(false);
      setToken(null);
      setIsLoading(false);
      return false;
    }
  }, []);

  const loginAdmin = useCallback(
    async ({ email, secret }) => {
      try {
        const adminUser = await loginAdminSession({ email, secret });

        persistStoredUser(ADMIN_USER_STORAGE_KEY, adminUser);
        setAdminHasRefreshCookie(true);

        const accessToken = await getAdminAccessToken(true);
        if (!accessToken) throw new Error("Failed to obtain admin access token");

        setIsAuthenticated(true);
        setUser({ ...adminUser, provider: "admin" });
        setToken(accessToken);
        setIsLoading(false);
        return { ok: true };
      } catch (err) {
        console.error("Admin login error:", err.message);
        removeStoredUser(ADMIN_USER_STORAGE_KEY);
        setAdminHasRefreshCookie(false);
        setIsLoading(false);
        return { ok: false, error: err };
      }
    },
    [getAdminAccessToken],
  );

  const loginStudent = useCallback(
    async ({ username, secret }) => {
      try {
        const studentUser = await loginStudentSession({ username, secret });

        persistStoredUser(STUDENT_USER_STORAGE_KEY, studentUser);
        setStudentHasRefreshCookie(true);

        const accessToken = await getStudentAccessToken(true);
        if (!accessToken) throw new Error("Failed to obtain student access token");

        setIsAuthenticated(true);
        setUser({ ...studentUser, provider: "student_gate" });
        setToken(accessToken);
        setIsLoading(false);
        return { ok: true };
      } catch (err) {
        console.error("Student login error:", err.message);
        removeStoredUser(STUDENT_USER_STORAGE_KEY);
        setStudentHasRefreshCookie(false);
        setIsLoading(false);
        return { ok: false, error: err };
      }
    },
    [getStudentAccessToken],
  );

  const loginFeide = useCallback((returnTo) => {
    const fallback = `${window.location.origin}/feide/callback`;
    let destination = fallback;
    if (returnTo) {
      try {
        destination = new URL(returnTo, window.location.origin).toString();
      } catch {
        destination = fallback;
      }
    }
    window.location.assign(
      `${BASE_URL}/feide/login?return_to=${encodeURIComponent(destination)}`,
    );
  }, []);

  const unifiedGetAccessTokenSilently = useCallback(
    async (options) => {
      if (user?.provider === "vipps") {
        return await getVippsAccessToken();
      }
      if (user?.provider === "feide") {
        return await getFeideAccessToken();
      }
      if (user?.provider === "admin") {
        const force =
          typeof options === "boolean"
            ? options
            : Boolean(options?.ignoreCache);
        return await getAdminAccessToken(force);
      }
      if (user?.provider === "student_gate") {
        const force =
          typeof options === "boolean"
            ? options
            : Boolean(options?.ignoreCache);
        return await getStudentAccessToken(force);
      }

      const auth0Options = AUTH0_AUDIENCE
        ? { audience: AUTH0_AUDIENCE, ...(options ?? {}) }
        : options;
      return await getAuth0AccessTokenWithTimeout(auth0Options);
    },
    [
      getAdminAccessToken,
      getAuth0AccessTokenWithTimeout,
      getFeideAccessToken,
      getStudentAccessToken,
      getVippsAccessToken,
      user,
    ],
  );

  const api = useMemo(
    () =>
      makeApiClient((force) =>
        isAuthenticated
          ? unifiedGetAccessTokenSilently(
              force ? { ignoreCache: true } : undefined,
            )
          : null,
      ),
    [isAuthenticated, unifiedGetAccessTokenSilently],
  );
  const publicApi = useMemo(() => makeApiClient(null, PUBLIC_BASE_URL), []);

  useEffect(() => {
    if (!auth0Loading) {
      setAuth0BootstrapTimedOut(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      console.error(
        `Auth0 initialization timed out after ${AUTH0_BOOTSTRAP_TIMEOUT_MS}ms`,
      );
      setAuth0BootstrapTimedOut(true);
    }, AUTH0_BOOTSTRAP_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [auth0Loading]);

  useEffect(() => {
    if (isLoggingOut) return;
    if (auth0Loading && !auth0BootstrapTimedOut) return;
    if (isAuthenticated && user?.provider && token) {
      setIsLoading(false);
      return;
    }
    if (bootstrapStartedRef.current) return;
    bootstrapStartedRef.current = true;

    const {
      adminUser: storedAdminUser,
      studentUser: storedStudentUser,
      feideUser: storedFeideUser,
      vippsUser: storedVippsUser,
    } = readStoredUserSnapshot();

    const tryAdmin = async () => {
      if (!storedAdminUser) return false;
      try {
        const parsedAdminUser = JSON.parse(storedAdminUser);
        setAdminHasRefreshCookie(true);
        const accessToken = await getAdminAccessToken(true);
        if (!accessToken) {
          removeStoredUser(ADMIN_USER_STORAGE_KEY);
          setAdminHasRefreshCookie(false);
          return false;
        }
        setIsAuthenticated(true);
        setUser({ ...parsedAdminUser, provider: "admin" });
        setToken(accessToken);
        setIsLoading(false);
        return true;
      } catch (err) {
        console.error("Error parsing admin user:", err);
        removeStoredUser(ADMIN_USER_STORAGE_KEY);
        setIsAuthenticated(false);
        setUser(null);
        setAdminHasRefreshCookie(false);
        setToken(null);
        setIsLoading(false);
        return false;
      }
    };

    const tryAuth0 = async () => {
      if (!auth0IsAuthenticated) return false;
      try {
        const accessToken = await getAuth0AccessTokenWithTimeout(
          AUTH0_AUDIENCE ? { audience: AUTH0_AUDIENCE } : undefined,
        );
        setIsAuthenticated(true);
        setUser({ ...auth0User, provider: "auth0" });
        setToken(accessToken);
        setIsLoading(false);
        return true;
      } catch (err) {
        console.error("Error fetching Auth0 token:", err);
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
        setIsLoading(false);
        return false;
      }
    };

    const tryStudent = async () => {
      if (!storedStudentUser) return false;
      try {
        const parsedStudentUser = JSON.parse(storedStudentUser);
        setStudentHasRefreshCookie(true);
        const accessToken = await getStudentAccessToken(true);
        if (!accessToken) {
          removeStoredUser(STUDENT_USER_STORAGE_KEY);
          setStudentHasRefreshCookie(false);
          return false;
        }
        setIsAuthenticated(true);
        setUser({ ...parsedStudentUser, provider: "student_gate" });
        setToken(accessToken);
        setIsLoading(false);
        return true;
      } catch (err) {
        console.error("Error parsing student user:", err);
        removeStoredUser(STUDENT_USER_STORAGE_KEY);
        setIsAuthenticated(false);
        setUser(null);
        setStudentHasRefreshCookie(false);
        setToken(null);
        setIsLoading(false);
        return false;
      }
    };

    const tryFeide = async () => {
      if (!storedFeideUser) return false;
      try {
        const parsedFeideUser = JSON.parse(storedFeideUser);
        setFeideHasRefreshCookie(true);
        const accessToken = await getFeideAccessToken(true);
        if (!accessToken) {
          removeStoredUser(FEIDE_USER_STORAGE_KEY);
          setFeideHasRefreshCookie(false);
          return false;
        }
        setIsAuthenticated(true);
        setUser({ ...parsedFeideUser, provider: "feide" });
        setToken(accessToken);
        setIsLoading(false);
        return true;
      } catch (err) {
        console.error("Error parsing Feide user:", err);
        removeStoredUser(FEIDE_USER_STORAGE_KEY);
        setIsAuthenticated(false);
        setUser(null);
        setFeideHasRefreshCookie(false);
        setToken(null);
        setIsLoading(false);
        return false;
      }
    };

    const tryVipps = async () => {
      if (!storedVippsUser) return false;
      try {
        const parsedVippsUser = JSON.parse(storedVippsUser);
        setVippsHasRefreshCookie(true);
        const accessToken = await getVippsAccessToken(true);
        if (!accessToken) {
          removeStoredUser(VIPPS_USER_STORAGE_KEY);
          setVippsHasRefreshCookie(false);
          return false;
        }
        setIsAuthenticated(true);
        setUser({ ...parsedVippsUser, provider: "vipps" });
        setToken(accessToken);
        setIsLoading(false);
        return true;
      } catch (err) {
        console.error("Error parsing Vipps user:", err);
        removeStoredUser(VIPPS_USER_STORAGE_KEY);
        setIsAuthenticated(false);
        setUser(null);
        setVippsHasRefreshCookie(false);
        setToken(null);
        setIsLoading(false);
        return false;
      }
    };

    (async () => {
      const adminOk = await tryAdmin();
      if (adminOk) return;
      const studentOk = await tryStudent();
      if (studentOk) return;
      const auth0Ok = await tryAuth0();
      if (auth0Ok) return;
      const feideOk = await tryFeide();
      if (feideOk) return;
      const vippsOk = await tryVipps();
      if (vippsOk) return;
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      setVippsHasRefreshCookie(false);
      setFeideHasRefreshCookie(false);
      setAdminHasRefreshCookie(false);
      setStudentHasRefreshCookie(false);
      setIsLoading(false);
    })();
  }, [
    auth0BootstrapTimedOut,
    auth0IsAuthenticated,
    auth0Loading,
    auth0User,
    getAdminAccessToken,
    getAuth0AccessTokenWithTimeout,
    getFeideAccessToken,
    getStudentAccessToken,
    getVippsAccessToken,
    isAuthenticated,
    isLoggingOut,
    token,
    user,
  ]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      isLoggingOut,
      isAuthenticated,
      user,
      token,
      login: (options) => loginWithRedirect(options),
      loginFeide,
      loginAdmin,
      loginStudent,
      logout: handleLogout,
      refreshToken,
      setVippsAuth,
      setFeideAuth,
      getVippsAccessToken,
      getFeideAccessToken,
      getAccessTokenSilently: unifiedGetAccessTokenSilently,
      api,
      publicApi,
    }),
    [
      api,
      getFeideAccessToken,
      getVippsAccessToken,
      handleLogout,
      isAuthenticated,
      isLoading,
      isLoggingOut,
      loginAdmin,
      loginFeide,
      loginStudent,
      loginWithRedirect,
      publicApi,
      refreshToken,
      setFeideAuth,
      setVippsAuth,
      token,
      unifiedGetAccessTokenSilently,
      user,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
